import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Download, KeyRound, Laptop, Loader2, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, saveBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SERVER = process.env.REACT_APP_BACKEND_URL;

function NewPassword({ created, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(created.password); setCopied(true); };
  return (
    <div className="border-2 border-emerald-600 bg-emerald-50 rounded-xl p-4" data-testid="sync-new-password">
      <div className="text-sm font-semibold text-emerald-900">Mot de passe de synchronisation pour « {created.label} »</div>
      <p className="text-xs text-emerald-800 mt-1">Copiez-le maintenant : il ne sera plus jamais affiché.</p>
      <div className="flex items-center gap-2 mt-3">
        <code className="flex-1 font-mono text-base bg-white border rounded px-3 py-2 select-all" data-testid="sync-password-value">{created.password}</code>
        <Button variant="outline" size="icon" onClick={copy} aria-label="Copier" data-testid="sync-password-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
      </div>
      <p className="text-xs text-emerald-800 mt-2">Identifiant : <strong>{created.username}</strong></p>
      <Button size="sm" className="mt-3" onClick={onDone} data-testid="sync-password-done">C'est noté</Button>
    </div>
  );
}

function Steps() {
  const steps = [
    "Téléchargez l'outil de synchronisation ci-dessous (fichier SDR-Sync.ps1) et placez-le par exemple dans Documents.",
    "Créez un mot de passe de synchronisation pour ce PC (un par ordinateur).",
    "Faites un clic droit sur SDR-Sync.ps1 > Propriétés > cochez « Débloquer » > OK (fichier venant d'Internet).",
    "Clic droit sur SDR-Sync.ps1 > « Exécuter avec PowerShell », puis saisissez votre email et ce mot de passe.",
    "Vos documents apparaissent dans le dossier « SDR PNEUS 91 » de votre profil Windows et se synchronisent toutes les minutes.",
    "Gros import (ex. 20 Go) : copiez simplement votre dossier dans le sous-dossier de la société voulue (dossier local « SDR PNEUS 91 » > nom de la société). L'envoi se fait en arrière-plan (fichiers jusqu'à 5 Go) et reprend tout seul après une coupure ou un redémarrage.",
    "Pour un démarrage automatique à chaque ouverture de session, ouvrez PowerShell dans ce dossier et lancez une fois : powershell -ExecutionPolicy Bypass -File .\\SDR-Sync.ps1 -Install",
  ];
  return (
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm"><span className="h-6 w-6 shrink-0 rounded-full bg-[#1E3A8A] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span><span className="text-slate-700 pt-0.5">{s}</span></li>
      ))}
    </ol>
  );
}

export function SyncPanel() {
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/dav-tokens").then((r) => setTokens(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/dav-tokens", { label }); setCreated(data); setLabel(""); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/dav-tokens/${id}`); toast.success("Accès révoqué"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const downloadScript = async () => {
    try {
      const { data } = await api.get("/sync/script.ps1", { responseType: "text" });
      saveBlob(new Blob(["\ufeff" + data.replace("__SERVER__", SERVER)], { type: "text/plain;charset=utf-8" }), "SDR-Sync.ps1");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6">
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-center gap-2"><Laptop className="h-5 w-5 text-[#1E3A8A]" /><h3 className="font-display font-bold">Synchroniser un PC Windows</h3></div>
        <p className="text-sm text-slate-600 mt-1 mb-5">Une copie de vos dossiers reste sur votre ordinateur, utilisable sans connexion. Les fichiers modifiés dans Word ou Excel sont renvoyés automatiquement comme nouvelle version.</p>
        <Steps />
        <Button className="mt-6" onClick={downloadScript} data-testid="sync-script-download"><Download className="h-4 w-4 mr-2" />Télécharger l'outil de synchronisation</Button>
        <p className="text-xs text-slate-500 mt-4">Les suppressions faites sur le PC envoient le document dans la corbeille en ligne (récupérable 30 jours). En cas de modification simultanée, votre version est conservée sous un nom « (conflit PC …) ».</p>
      </div>
      <div className="space-y-4">
        {created && <NewPassword created={created} onDone={() => setCreated(null)} />}
        <form onSubmit={create} className="bg-white border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 font-semibold text-sm"><KeyRound className="h-4 w-4 text-[#1E3A8A]" />Nouveau mot de passe de synchronisation</div>
          <Input required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nom du PC, ex. PC bureau atelier" data-testid="sync-label-input" />
          <Button type="submit" className="w-full" disabled={busy} data-testid="sync-create-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Créer</Button>
        </form>
        <div className="bg-white border rounded-xl divide-y" data-testid="sync-tokens-list">
          <div className="px-4 py-3 text-sm font-semibold">Ordinateurs autorisés</div>
          {tokens.length === 0 && <p className="px-4 py-4 text-sm text-slate-500">Aucun ordinateur pour le moment.</p>}
          {tokens.map((t) => (
            <div key={t.id} className="px-4 py-3 flex items-center gap-3" data-testid={`sync-token-${t.id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{t.label}</div>
                <div className="text-xs text-slate-500">Créé le {fmtDate(t.created_at)} · dernière synchro {t.last_used_at ? fmtDate(t.last_used_at, true) : "jamais"}</div></div>
              <Button variant="ghost" size="icon" aria-label="Révoquer" onClick={() => revoke(t.id)} data-testid={`sync-token-revoke-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
